import { createSlideHeightAnimationComponent } from '@avensia/excite-ui';
import { type CheckboxFieldPropType, useCheckboxField, useCombineEventHandlers } from '@avensia/excite-ui';
import { styled } from '@glitz/react';
import * as React from 'react';
import { CheckIcon as CheckEl } from '../icons';
import { white } from 'webapp/shared/sharedTheme';

export enum CheckboxAlign {
  Left,
  Right,
}

type FieldPropType = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value'> &
  CheckboxFieldPropType & {
    checkboxAlign?: CheckboxAlign;
    radio?: boolean;
    flexFieldLabel?: boolean;
  };

export type { FieldPropType as CheckboxFieldPropType };
export const CheckboxField = styled(
  React.forwardRef(
    (
      {
        children,
        error,
        validate,
        checked,
        checkboxAlign = CheckboxAlign.Left,
        radio = false,
        flexFieldLabel = true,
        ...restProps
      }: React.PropsWithChildren<FieldPropType>,
      ref: React.Ref<HTMLInputElement>,
    ) => {
      const elementRef = React.useRef<HTMLInputElement>(null);
      React.useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(ref, () => elementRef.current);

      const { invalid, validationMessage, inputProps } = useCheckboxField(elementRef, checked, { error, validate });

      const onChange = useCombineEventHandlers(restProps.onChange, inputProps.onChange);
      const onInvalid = useCombineEventHandlers(restProps.onInvalid, inputProps.onInvalid);

      const dynamic = React.useMemo(() => styled({ width: '100%' }), []);

      return (
        <Label>
          <Input
            type={radio ? 'radio' : 'checkbox'}
            {...restProps}
            {...inputProps}
            onChange={onChange}
            onInvalid={onInvalid}
            css={dynamic}
            ref={elementRef}
          />
          <Field>
            {checkboxAlign !== CheckboxAlign.Left && children && (
              <FieldLabelLeft css={flexFieldLabel && flexDecorator}>{children}</FieldLabelLeft>
            )}

            {radio ? (
              <Radio
                css={styled(inputProps.checked ? checkedRadioDecorator : {}, restProps.disabled && disabledDecorator)}
              />
            ) : (
              <Check
                css={styled(
                  inputProps.checked ? checkedDecorator : uncheckedDecorator,
                  restProps.disabled && disabledDecorator,
                )}
              />
            )}

            {checkboxAlign === CheckboxAlign.Left && children && (
              <FieldLabelRight css={flexFieldLabel && flexDecorator}>{children}</FieldLabelRight>
            )}
          </Field>
          <ValidationMessage visible={invalid}>{validationMessage}</ValidationMessage>
        </Label>
      );
    },
  ),
);

const Input = styled.input({
  position: 'absolute',
  top: 0,
  left: 0,
  appearance: 'none',
  margin: { xy: 0 },
  width: '100%',
  height: '100%',
  placeContent: 'center',
  ':focus-visible': {
    outline: { width: 2, style: 'solid', offset: 2, color: 'black' },
  },
});

const Check = styled(CheckEl, {
  color: t => t.checkbox.color,
  borderRadius: t => t.checkbox.borderRadius,
  strokeWidth: 3,
});

const Label = styled.label({
  position: 'relative',
  display: 'block',
});

const Field = styled.div({
  display: 'inline-flex',
  alignItems: 'center',
  verticalAlign: 'middle',
  width: 'inherit',
  cursor: 'pointer',
  position: 'relative',
});

const Radio = styled.div({
  borderRadius: '50%',
  minHeight: '1.5em',
  minWidth: '1.5em',
  color: t => t.dark.text,
  border: { xy: { color: t => t.checkbox?.borderColor, style: 'solid', width: 1 } },
});

const checkedRadioDecorator = styled({
  backgroundColor: t => t.checkbox?.checkedBackgroundColor,
  boxShadow: `inset 0 0 0 3px ${white}`,
});

const uncheckedDecorator = styled({
  color: 'transparent',
  backgroundColor: 'transparent',
  border: { xy: { color: t => t.checkbox?.borderColor, style: 'solid', width: 'thin' } },
  width: '1.32em',
  height: '1.32em',
});

const checkedDecorator = styled({
  color: t => t.checkbox?.checkedColor,
  backgroundColor: t => t.checkbox?.checkedBackgroundColor,
  border: { xy: { color: t => t.checkbox.checkedBackgroundColor, style: 'solid', width: 'thin' } },
  padding: { xy: 1 },
  width: '1.32em',
  height: '1.32em',
});

const disabledDecorator = styled({
  backgroundColor: t => t.checkbox.disabled,
  color: t => t.disabled.text,
  border: { xy: { color: t => t.disabled.text } },
  padding: { xy: 1 },
  width: '1.32em',
  height: '1.32em',
});

const FieldLabel = styled.span({
  fontSize: t => t.fontSize.xs,
  width: 'inherit',
});

const flexDecorator = styled({
  display: 'flex',
  alignItems: 'center',
});

const FieldLabelLeft = styled(FieldLabel, {
  marginRight: t => t.spacing.s,
});

const FieldLabelRight = styled(FieldLabel, {
  marginLeft: t => t.spacing.s,
});

export const ValidationMessage = createSlideHeightAnimationComponent(
  styled.span({
    display: 'block',
    fontSize: t => t.fontSize.xs,
    color: t => t.negative.color,
  }),
);
